import { TwoColumnsLeft, SixColumns, TwoRows } from 'svg';
import { LayoutId, WeekdayTitleStyle, WeekdayTitlePosition } from './constants';

/**
 * Layout Configuration
 * Defines available layouts with their metadata and capabilities
 */
export const LAYOUT_CONFIG = [
  {
    id: LayoutId.TWO_COLUMNS_LEFT,
    element: TwoColumnsLeft,
    description: 'Hai cột trái',
    type: 'Tùy chỉnh',
    capabilities: {
      canChangeTitleStyle: true, // Can change between pinned/normal
      canChangeTitlePosition: true, // Can change top/bottom
    },
  },
  {
    id: LayoutId.SIX_COLUMNS,
    element: SixColumns,
    description: '1 hàng ngang',
    type: 'Tùy chỉnh',
    capabilities: {
      canChangeTitleStyle: true,
      canChangeTitlePosition: true,
    },
  },
  {
    id: LayoutId.TWO_ROWS,
    element: TwoRows,
    description: 'Hai hàng',
    type: 'Cố định',
    capabilities: {
      canChangeTitleStyle: false, // Fixed to NORMAL style
      canChangeTitlePosition: true,
    },
  },
] as const;

/**
 * Helper function to get layout configuration by ID
 */
export const getLayoutConfig = (layoutId: LayoutId) => {
  return LAYOUT_CONFIG.find((layout) => layout.id === layoutId);
};

/**
 * Weekday Title Style Options
 */
export const WEEKDAY_TITLE_STYLE_OPTIONS = [
  { value: WeekdayTitleStyle.PINNED, label: 'Ghim' },
  { value: WeekdayTitleStyle.NORMAL, label: 'Thường' },
] as const;

/**
 * Weekday Title Position Options
 */
export const WEEKDAY_TITLE_POSITION_OPTIONS = [
  { value: WeekdayTitlePosition.TOP, label: 'Trên' },
  { value: WeekdayTitlePosition.BOTTOM, label: 'Dưới' },
] as const;

/**
 * Logo Configuration
 */
export const LOGO_CONFIG = [
  {
    label: 'Logo Cơ quan chủ quản',
    settingKey: 'hvs' as const,
  },
  {
    label: 'Logo Khách hàng',
    settingKey: 'client' as const,
  },
] as const;

/**
 * Background Theme Configuration
 */
export const THEME_LIST = [
  { id: 1, name: 'Ảnh chung' },
  { id: 2, name: '20/10' },
  { id: 3, name: 'Tết' },
] as const;

/**
 * Background Image Mapping
 * Maps category and image ID to actual image file names (KEM duoi file).
 * Duoi file nam trong gia tri chu khong ghep cung o cho dung: anh nen co ca .webp/.jpg/.png,
 * ep het ve .png chi de hop voi chuoi ghep cung se phinh dung luong gap ~4 lan ma khong dep hon,
 * vi PNG luu lai y nguyen thu webp/jpg da nen san bang mot dinh dang cong kenh hon.
 */
export const BACKGROUND_IMAGES: Record<number, Record<number, string>> = {
  1: {
    1: 'menu_bg_1.webp',
    2: 'menu_bg_2.webp',
    3: 'menu_bg_3.webp',
    4: 'menu_bg_4.webp',
    5: 'menu_bg_5.webp',
    6: 'menu_bg_6.png',
    7: 'menu_bg_7.webp',
    8: 'menu_bg_8.png',
    9: 'menu_bg_9.png',
    10: 'menu_bg_10.webp',
    11: 'menu_bg_11.webp',
    12: 'menu_bg_12.webp',
    13: 'menu_bg_13.webp',
    14: 'menu_bg_14.webp',
    15: 'menu_bg_15.webp',
    16: 'menu_bg_16.webp',
    17: 'menu_bg_17.webp',
    18: 'menu_bg_18.webp',
    19: 'menu_bg_19.jpg',
    20: 'menu_bg_20.jpg',
    21: 'menu_bg_21.webp',
    22: 'menu_bg_22.jpg',
    23: 'menu_bg_23.webp',
    24: 'menu_bg_24.jpg',
    25: 'menu_bg_25.jpg',
    26: 'menu_bg_26.jpg',
    27: 'menu_bg_27.jpg',
    28: 'menu_bg_28.jpg',
    29: 'menu_bg_29.jpg',
    30: 'menu_bg_30.jpg',
    31: 'menu_bg_31.jpg',
    32: 'menu_bg_32.jpg',
    33: 'menu_bg_33.jpg',
    34: 'menu_bg_34.jpg',
    35: 'menu_bg_35.jpg',
    36: 'menu_bg_36.webp',
    37: 'menu_bg_37.webp',
    38: 'menu_bg_38.webp',
    39: 'menu_bg_39.webp',
    40: 'menu_bg_40.webp',
    41: 'menu_bg_41.webp',
    42: 'menu_bg_42.webp',
    43: 'menu_bg_43.webp',
    44: 'menu_bg_44.webp',
    45: 'menu_bg_45.png',
    46: 'menu_bg_46.webp',
    47: 'menu_bg_47.webp',
    48: 'menu_bg_48.png',
    49: 'menu_bg_49.webp',
    50: 'menu_bg_50.webp',
    51: 'menu_bg_51.webp',
    52: 'menu_bg_52.jpg',
    53: 'menu_bg_53.webp',
    54: 'menu_bg_54.webp',
  },
  2: {
    6: 'menu_bg_6.png',
    7: 'menu_bg_7.webp',
    8: 'menu_bg_8.png',
  },
  3: {
    16: 'menu_bg_16.webp',
    17: 'menu_bg_17.webp',
    18: 'menu_bg_18.webp',
  },
};

interface BackgroundImageInfo {
  id: keyof (typeof BACKGROUND_IMAGES)[number];
  name: string;
}

export const mapBackgroundImagesByCategory = (): {
  category: number;
  images: BackgroundImageInfo[];
}[] => {
  const backgroundImagesByCategory: Array<{
    category: number;
    images: BackgroundImageInfo[];
  }> = [];

  for (const category in BACKGROUND_IMAGES) {
    const imagesByCategory = BACKGROUND_IMAGES[parseInt(category)];
    const images: BackgroundImageInfo[] = [];

    for (const id in imagesByCategory) {
      images.push({ id: parseInt(id), name: imagesByCategory[id] });
    }
    backgroundImagesByCategory.push({ category: parseInt(category), images });
  }

  return backgroundImagesByCategory;
};

/**
 * Background Theme Images Configuration
 * Organized by category with their respective images
 */
export const BACKGROUND_THEME_IMAGES = mapBackgroundImagesByCategory();

/**
 * Menu Settings Limits
 */
export const MENU_LIMITS = {
  borderWidth: { min: 0, max: 20 },
  borderRadius: { min: 0, max: 130 },
  dishLimit: 5,
  afternoonLimit: 2,
} as const;

/**
 * Font Size Limits per Section
 */
export const FONT_SIZE_LIMITS = {
  weekTitle: { min: 24, max: 70 },
  subWeekTitle: { min: 22, max: 52 },
  weekdayTitle: { min: 16, max: 42 },
  content: { min: 10, max: 38 },
} as const;

/** Thư mục ảnh nền và thư mục ảnh thu nhỏ đi kèm. */
const BACKGROUND_DIR = '/menu-background_image';
const BACKGROUND_THUMB_DIR = `${BACKGROUND_DIR}/thumbs`;

/**
 * Đường dẫn ảnh THU NHỎ dùng cho ô chọn nền.
 *
 * Vì sao cần: ô chọn vẽ MỌI ảnh của nhóm đang chọn cùng một lúc. Nhóm 1 có 54 ảnh cỡ tới
 * 2560x1440; bắt trình duyệt giải nén từng ấy ảnh cỡ thật chỉ để hiện ô xem trước rộng ~240px là
 * phí vô ích và đủ sức làm treo tab trên máy yếu (đã gặp thật khi thử tải cả 54 ảnh cùng lúc).
 *
 * Ảnh thu nhỏ là 480x270 WebP (~12 KB/ảnh, cả bộ 0,65 MB so với 11,7 MB ảnh đầy đủ), cắt giữa
 * theo tỉ lệ 16:9 cho khớp với backgroundSize: cover của ô xem trước. Ảnh đầy đủ chỉ được tải khi
 * người dùng đã chọn, lúc mẫu thực đơn vẽ ra.
 *
 * Thêm ảnh nền mới thì nhớ tạo kèm ảnh thu nhỏ cùng tên, đuôi .webp, trong thư mục thumbs/.
 * Quên cũng không vỡ giao diện — xem chú thích ở BackgroundSelector.
 */
export const getBackgroundThumbPath = (fileName: string): string => {
  const khongDuoi = fileName.replace(/\.[^.]+$/, '');
  return `${BACKGROUND_THUMB_DIR}/${khongDuoi}.webp`;
};

/**
 * Helper function to get background image path
 */
export const getBackgroundImagePath = (category: number, id: number): string | null => {
  const imageName = BACKGROUND_IMAGES[category]?.[id];
  return imageName ? `${BACKGROUND_DIR}/${imageName}` : null;
};
